import { useCallback, useEffect, useState } from "react"
import { checkForServiceWorkerUpdate } from "@/lib/register-sw"

export interface SWUpdateResult {
  updateAvailable: boolean
  applyUpdate: () => Promise<void>
  checkForUpdate: () => Promise<void>
}

export function useSWUpdate(): SWUpdateResult {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null)
  const [updateAvailable, setUpdateAvailable] = useState(false)

  useEffect(() => {
    const onUpdate = (event: Event) => {
      const custom = event as CustomEvent<ServiceWorkerRegistration>
      setRegistration(custom.detail)
      setUpdateAvailable(true)
    }
    window.addEventListener("sw-update-available", onUpdate)
    return () => window.removeEventListener("sw-update-available", onUpdate)
  }, [])

  const applyUpdate = useCallback(async () => {
    const waiting = registration?.waiting
    if (!waiting) return

    await new Promise<void>((resolve) => {
      const onControllerChange = () => {
        navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange)
        resolve()
      }
      navigator.serviceWorker.addEventListener("controllerchange", onControllerChange)
      waiting.postMessage({ type: "SKIP_WAITING" })
    })

    window.location.reload()
  }, [registration])

  const checkForUpdate = useCallback(async () => {
    if (!registration || !("update" in registration)) return
    await checkForServiceWorkerUpdate(registration as ServiceWorkerRegistration)
  }, [registration])

  return { updateAvailable, applyUpdate, checkForUpdate }
}
